/**
 * Ajax Superior line: the products of ajax.systems/catalogue/superior/ that
 * the Ajax seed (ajaxAndHanwhaCatalog.ts) did not carry yet. Same rule as
 * that seed: every value comes from the product's spec page, cited above
 * the entry; a field the page does not state is left out. Metric values are
 * the ones Ajax publishes on its French pages, not conversions.
 *
 * Fibra bus modules (transmitters, splitter, line protection, line supply,
 * relays) ride the 'wireless-expander' category, like Transmitter Jeweller:
 * it is the panel-side bus device the designer places with the panel glyph.
 * LineSupply's output voltage is not published, so it is not modelled as a
 * power supply with an invented voltage.
 *
 * Read date: 2026-10-13.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

const SUPERIOR_JEWELLER_BANDS = '865.0–868.0 / 868.0–868.6 / 868.7–869.2 / 869.4–869.65 MHz (Superior Jeweller)';

export const AJAX_SUPERIOR_CATALOG: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════
  // Control panels and range extender
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/fr/products/specs/superior-hub-g3-jeweller/
  {
    id: 'ajax-superior-hub-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior Hub G3 Jeweller',
    category: 'alarm-panel',
    tagline: 'Grade 3 wireless hub, 250 devices, 25 groups, 200 users, Ethernet + Wi-Fi + 2× SIM 4G',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-hub-g3-jeweller/',
    specs: {
      series: 'Superior Hub G3',
      zonesOnboard: 0,
      zonesMax: 250,
      partitions: 25,
      users: 200,
      grade: 3,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'Ajax Security System',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-megahub/
  {
    id: 'ajax-superior-megahub',
    manufacturer: 'Ajax',
    model: 'Superior MegaHub',
    category: 'alarm-panel',
    tagline: 'Grade 3 hybrid hub, 999 Fibra and Jeweller devices, 8 Fibra lines, 100 groups, 1,000 users, 18 Ah battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-megahub/',
    specs: {
      series: 'Superior MegaHub',
      zonesOnboard: 0,
      zonesMax: 999,
      partitions: 100,
      users: 1000,
      grade: 3,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      psuAmps: 1.6, // Fibra output 22–25 V⎓, up to 1.6 A total
      mobileApp: 'Ajax Security System',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-hub-hybrid-2/
  {
    id: 'ajax-superior-hub-hybrid-2',
    manufacturer: 'Ajax',
    model: 'Superior Hub Hybrid 2',
    category: 'alarm-panel',
    tagline: 'Grade 3 hybrid hub, 250 Fibra and Jeweller devices, 8 Fibra lines, 25 groups, Ethernet + 2× SIM, 7 Ah battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-hub-hybrid-2/',
    specs: {
      series: 'Superior Hub Hybrid 2',
      zonesOnboard: 0,
      zonesMax: 250,
      partitions: 25,
      users: 200,
      grade: 3,
      communicators: ['Ethernet', 'GSM-LTE'],
      psuAmps: 0.53, // 23–25.1 V⎓, 0.53 A max
      mobileApp: 'Ajax Security System',
    },
  },

  // https://ajax.systems/products/specs/superior-hub-hybrid-4g-without-casing/
  // (the same board as the cased (4G) version)
  {
    id: 'ajax-superior-hub-hybrid-4g',
    manufacturer: 'Ajax',
    model: 'Superior Hub Hybrid (4G)',
    category: 'alarm-panel',
    tagline: 'Grade 3 hybrid hub, 100 devices, 8 Fibra lines, 9 groups, 50 users, Ethernet + 2× SIM 2G/3G/LTE',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/superior-hub-hybrid/',
    specs: {
      series: 'Superior Hub Hybrid',
      zonesOnboard: 0,
      zonesMax: 100,
      partitions: 9,
      users: 50,
      grade: 3,
      communicators: ['Ethernet', 'GSM-LTE'],
      mobileApp: 'Ajax Security System',
    },
  },

  // https://ajax.systems/products/specs/superior-hub-hybrid-2g/
  {
    id: 'ajax-superior-hub-hybrid-2g',
    manufacturer: 'Ajax',
    model: 'Superior Hub Hybrid (2G)',
    category: 'alarm-panel',
    tagline: 'Grade 3 hybrid hub, 100 devices, 8 Fibra lines, 9 groups, 50 users, Ethernet + 2× SIM 2G',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-hub-hybrid-2g/',
    specs: {
      series: 'Superior Hub Hybrid',
      zonesOnboard: 0,
      zonesMax: 100,
      partitions: 9,
      users: 50,
      grade: 3,
      communicators: ['Ethernet', 'GSM-LTE'],
      mobileApp: 'Ajax Security System',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-rex-g3-jeweller/
  {
    id: 'ajax-superior-rex-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior ReX G3 Jeweller',
    category: 'wireless-expander',
    tagline: 'Grade 3 radio range extender with Ethernet link to the hub and audio verification support, up to 3,500 m',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-rex-g3-jeweller/',
    specs: {
      frequency: SUPERIOR_JEWELLER_BANDS,
      biDirectional: true,
      busConnection: 'Superior Jeweller radio or Ethernet to the hub',
      rangeM: 3500,
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // Motion detectors
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/specs/superior-motionprotect-g3-jeweller/
  {
    id: 'ajax-superior-motionprotect-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior MotionProtect G3 Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: false,
    batteryOperated: true, // 4× CR123A, up to 7 years
    ipRating: 'IP30',
    operatingTempMinC: -10,
    operatingTempMaxC: 40,
    tagline: 'Grade 3 wireless PIR with anti-masking, 15 m / 90°, 4× CR123A up to 7 years',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motionprotect-g3-jeweller/',
  },

  // https://ajax.systems/fr/products/specs/superior-motionprotect-plus-g3-jeweller/
  {
    id: 'ajax-superior-motionprotect-plus-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior MotionProtect Plus G3 Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual', // PIR + K-band microwave
    antiMasking: true,
    petImmune: false,
    batteryOperated: true, // 4× CR123A, up to 4 years
    ipRating: 'IP30',
    operatingTempMinC: -10,
    operatingTempMaxC: 40,
    tagline: 'Grade 3 wireless PIR + K-band microwave, anti-masking, 15 m / 90°',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motionprotect-plus-g3-jeweller/',
  },

  // https://ajax.systems/products/specs/superior-motioncam-g3-phod-jeweller/
  {
    id: 'ajax-superior-motioncam-g3-phod-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior MotionCam G3 (PhOD) Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir', // PIR + verification camera (photo on demand)
    antiMasking: true,
    batteryOperated: true, // 4× CR123A, up to 6 years
    ipRating: 'IP30',
    operatingTempMinC: -10,
    operatingTempMaxC: 40,
    tagline: 'Grade 3 wireless PIR with photo verification (864 × 720) and anti-masking, 15 m / 90°',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motioncam-g3-phod-jeweller/',
  },

  // https://ajax.systems/fr/products/specs/superior-motioncam-hd-phod-jeweller/
  {
    id: 'ajax-superior-motioncam-hd-phod-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior MotionCam HD (PhOD) Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 12,
    hfovDeg: 88.5, // motion detection angle (camera 90° × 50°)
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // up to 20 kg / 50 cm
    batteryOperated: true, // 2× CR123A, up to 4 years
    ipRating: 'IP50',
    operatingTempMinC: -10,
    operatingTempMaxC: 40,
    tagline: 'Wireless PIR with photo verification up to 960 × 720, pet immune, 12 m / 88.5°, IP50',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motioncam-hd-phod-jeweller/',
  },

  // https://ajax.systems/fr/products/specs/superior-motioncam-am-phod-jeweller/
  {
    id: 'ajax-superior-motioncam-am-phod-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior MotionCam AM (PhOD) Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 12,
    hfovDeg: 92,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: false,
    batteryOperated: true, // 2× CR123A, up to 4 years
    ipRating: 'IP50',
    operatingTempMinC: -10,
    operatingTempMaxC: 40,
    tagline: 'Wireless PIR with photo verification up to 960 × 720 and anti-masking, 12 m / 92°, IP50',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motioncam-am-phod-jeweller/',
  },

  // ═══════════════════════════════════════════════════════════════════
  // Opening and seismic detectors
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/fr/products/specs/superior-doorprotect-g3-jeweller/
  {
    id: 'ajax-superior-doorprotect-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior DoorProtect G3 Jeweller',
    category: 'magnetic-contact',
    tagline: 'Grade 3 wireless reed contact with shock/tilt accelerometer and magnetic-masking sensor, NC input, CR123A up to 4 years',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-doorprotect-g3-jeweller/',
    specs: {
      mountType: 'surface', // SmartBracket panel
      housing: 'plastic',
      tamper: true,
      dimensionsMm: '103 × 22 × 21',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-doorprotect-g3-fibra/
  {
    id: 'ajax-superior-doorprotect-g3-fibra',
    manufacturer: 'Ajax',
    model: 'Superior DoorProtect G3 Fibra',
    category: 'magnetic-contact',
    tagline: 'Grade 3 wired Fibra reed contact with shock/tilt accelerometer and magnetic-masking sensor, 2 tampers',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-doorprotect-g3-fibra/',
    specs: {
      mountType: 'surface', // mounting panel
      housing: 'plastic',
      tamper: true,
      dimensionsMm: '102 × 21 × 20',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-seismoprotect-g3-fibra/
  // A seismic (vibration) detector: the glass-break category's piezo-shock
  // method is the catalog's vibration detector, radius as published for concrete.
  {
    id: 'ajax-superior-seismoprotect-g3-fibra',
    manufacturer: 'Ajax',
    model: 'Superior SeismoProtect G3 Fibra',
    category: 'glass-break',
    tagline: 'Grade 3 wired Fibra seismic detector: drilling, cutting, explosions and forced bending; radius 4 m in concrete, 2 m in steel; IP43',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-seismoprotect-g3-fibra/',
    specs: {
      method: 'piezo-shock',
      rangeM: 4,
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // Keypads
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/fr/products/specs/superior-keypad-plus-g3-jeweller/
  {
    id: 'ajax-superior-keypad-plus-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior KeyPad Plus G3 Jeweller',
    category: 'keypad',
    tagline: 'Grade 3 wireless touch keypad with DESFire reader, 4× AA up to 4.5 years, IK06',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-keypad-plus-g3-jeweller/',
    specs: {
      display: 'touchscreen', // capacitive touch keys
      connection: 'Superior Jeweller',
      readerType: 'mifare', // DESFire EV1/EV2/EV3
      compatibility: [],
      grade: 3,
    },
  },

  // https://ajax.systems/fr/products/specs/superior-keypad-plus-jeweller/
  {
    id: 'ajax-superior-keypad-plus-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior KeyPad Plus Jeweller',
    category: 'keypad',
    tagline: 'Wireless touch keypad with Pass/Tag reader, 4× AA up to 3 years',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-keypad-plus-jeweller/',
    specs: {
      display: 'touchscreen', // capacitive touch keys
      connection: 'Jeweller',
      readerType: 'mifare', // Pass cards and Tag fobs
      compatibility: [],
      grade: 2,
    },
  },

  // https://ajax.systems/fr/products/specs/superior-keypad-touchscreen-fibra/
  {
    id: 'ajax-superior-keypad-touchscreen-fibra',
    manufacturer: 'Ajax',
    model: 'Superior KeyPad TouchScreen Fibra',
    category: 'keypad',
    tagline: 'Grade 3 wired Fibra keypad, 5-inch touchscreen, DESFire cards and Bluetooth smartphone access',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-keypad-touchscreen-fibra/',
    specs: {
      display: 'touchscreen', // 5″
      connection: 'Fibra',
      readerType: 'mifare', // DESFire EV1/EV2 + BLE
      compatibility: [],
      grade: 3,
    },
  },

  // https://ajax.systems/fr/products/specs/superior-keypad-touchscreen-g3-jeweller/
  {
    id: 'ajax-superior-keypad-touchscreen-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior KeyPad TouchScreen G3 Jeweller',
    category: 'keypad',
    tagline: 'Grade 3 wireless keypad, 5-inch touchscreen, DESFire cards and Bluetooth smartphone access, 6× AA',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-keypad-touchscreen-g3-jeweller/',
    specs: {
      display: 'touchscreen', // 5″
      connection: 'Superior Jeweller',
      readerType: 'mifare', // DESFire EV1/EV2 + BLE
      compatibility: [],
      grade: 3,
    },
  },

  // https://ajax.systems/fr/products/specs/superior-keypad-outdoor-fibra/
  {
    id: 'ajax-superior-keypad-outdoor-fibra',
    manufacturer: 'Ajax',
    model: 'Superior KeyPad Outdoor Fibra',
    category: 'keypad',
    tagline: 'Grade 3 outdoor wired Fibra keypad with DESFire reader, IP66 / IK08, −25 to +60 °C',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-keypad-outdoor-fibra/',
    specs: {
      display: 'led', // no screen listed on the spec page
      connection: 'Fibra',
      readerType: 'mifare', // DESFire EV1/EV2/EV3
      compatibility: [],
      grade: 3,
    },
  },

  // https://ajax.systems/fr/products/specs/superior-keypad-fibra/
  {
    id: 'ajax-superior-keypad-fibra',
    manufacturer: 'Ajax',
    model: 'Superior KeyPad Fibra',
    category: 'keypad',
    tagline: 'Grade 3 wired Fibra touch keypad, powered by the hub',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-keypad-fibra/',
    specs: {
      display: 'touchscreen', // capacitive touch keys
      connection: 'Fibra',
      compatibility: [],
      grade: 3,
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // Sirens
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/fr/products/specs/superior-streetsiren-doubledeck-jeweller/
  {
    id: 'ajax-superior-streetsiren-doubledeck-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior StreetSiren DoubleDeck Jeweller',
    category: 'outdoor-siren',
    tagline: 'Indoor/outdoor wireless siren, 85–113 dB @ 1 m, IP54, 4× CR123A or external 12 V',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-streetsiren-doubledeck-jeweller/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 113,
      grade: 2,
      batteryBackup: 'built-in',
      ipRating: 'IP54',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-streetsiren-plus-g3-jeweller/
  {
    id: 'ajax-superior-streetsiren-plus-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior StreetSiren Plus G3 Jeweller',
    category: 'outdoor-siren',
    tagline: 'Grade 3 indoor/outdoor wireless siren, 85–107 dB @ 1 m, IP54 / IK08, 8× CR123A or external 12 V',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-streetsiren-plus-g3-jeweller/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 107,
      grade: 3,
      batteryBackup: 'built-in',
      ipRating: 'IP54',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-streetsiren-plus-jeweller/
  {
    id: 'ajax-superior-streetsiren-plus-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior StreetSiren Plus Jeweller',
    category: 'outdoor-siren',
    tagline: 'Indoor/outdoor wireless siren, 85–107 dB @ 1 m, IP54 / IK08, 8× CR123A up to 10 years or external 12 V',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-streetsiren-plus-jeweller/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 107,
      grade: 2,
      batteryBackup: 'built-in',
      ipRating: 'IP54',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-streetsiren-doubledeck-fibra/
  {
    id: 'ajax-superior-streetsiren-doubledeck-fibra',
    manufacturer: 'Ajax',
    model: 'Superior StreetSiren DoubleDeck Fibra',
    category: 'outdoor-siren',
    tagline: 'Grade 3 wired Fibra indoor/outdoor siren, 85–113 dB @ 1 m, LED frame, IP54, LTO backup battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-streetsiren-doubledeck-fibra/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 113,
      grade: 3,
      batteryBackup: 'built-in', // rechargeable LTO 2.4 V, 1,500 mAh
      ipRating: 'IP54',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-streetsiren-fibra/
  {
    id: 'ajax-superior-streetsiren-fibra',
    manufacturer: 'Ajax',
    model: 'Superior StreetSiren Fibra',
    category: 'outdoor-siren',
    tagline: 'Grade 3 wired Fibra indoor/outdoor siren, 85–113 dB @ 1 m, LED frame, IP54',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-streetsiren-fibra/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 113,
      grade: 3,
      batteryBackup: 'built-in', // rechargeable LTO 2.4 V, 1,500 mAh
      ipRating: 'IP54',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-streetsiren-plus-fibra/
  {
    id: 'ajax-superior-streetsiren-plus-fibra',
    manufacturer: 'Ajax',
    model: 'Superior StreetSiren Plus Fibra',
    category: 'outdoor-siren',
    tagline: 'Grade 3 wired Fibra indoor/outdoor siren, 85–107 dB @ 1 m, IP54 / IK08, LTO 3,000 mAh backup',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-streetsiren-plus-fibra/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 107,
      grade: 3,
      batteryBackup: 'built-in',
      ipRating: 'IP54',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-homesiren-g3-jeweller/
  {
    id: 'ajax-superior-homesiren-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior HomeSiren G3 Jeweller',
    category: 'indoor-sounder',
    tagline: 'Grade 3 indoor wireless siren, 81–105 dB @ 1 m, external LED output, 2× CR123A',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-homesiren-g3-jeweller/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 105,
      grade: 3,
      batteryBackup: 'built-in',
      ipRating: 'IP30',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-homesiren-jeweller/
  {
    id: 'ajax-superior-homesiren-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior HomeSiren Jeweller',
    category: 'indoor-sounder',
    tagline: 'Indoor wireless siren, 81–105 dB @ 1 m, external LED output, 2× CR123A up to 5 years',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-homesiren-jeweller/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 105,
      grade: 2,
      batteryBackup: 'built-in',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-homesiren-fibra/
  {
    id: 'ajax-superior-homesiren-fibra',
    manufacturer: 'Ajax',
    model: 'Superior HomeSiren Fibra',
    category: 'indoor-sounder',
    tagline: 'Grade 3 wired Fibra indoor siren, 72–89 dB @ 1 m, powered by the hub, external LED output',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-homesiren-fibra/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 89,
      grade: 3,
      batteryBackup: 'none',
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // Buttons and key fob
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/fr/products/specs/superior-button-jeweller/
  {
    id: 'ajax-superior-button-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior Button Jeweller',
    category: 'panic-button',
    tagline: 'Wireless panic button, long or double press against accidental alarms, CR2032 up to 5 years',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-button-jeweller/',
    specs: {
      memory: 'momentary',
      connection: 'wireless',
      wirelessSystem: 'Jeweller',
      accidentalPressProtection: true,
      tamper: true,
    },
  },

  // https://ajax.systems/fr/products/specs/superior-doublebutton-g3-jeweller/
  {
    id: 'ajax-superior-doublebutton-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior DoubleButton G3 Jeweller',
    category: 'panic-button',
    tagline: 'Grade 3 two-button hold-up device, both buttons needed, protective separator, CR2032 up to 5 years',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-doublebutton-g3-jeweller/',
    specs: {
      memory: 'momentary',
      connection: 'wireless',
      wirelessSystem: 'Jeweller', // Superior Jeweller
      accidentalPressProtection: true,
      tamper: true,
    },
  },

  // https://ajax.systems/fr/products/specs/ajax-superior-spacecontrol-jeweller/
  {
    id: 'ajax-superior-spacecontrol-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior SpaceControl Jeweller',
    category: 'panic-button',
    tagline: 'Key fob with 4 buttons: arm, disarm, night mode and panic, CR2032 up to 5 years',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/ajax-superior-spacecontrol-jeweller/',
    specs: {
      memory: 'momentary',
      connection: 'wireless',
      wirelessSystem: 'Jeweller',
      accidentalPressProtection: true,
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // Integration and Fibra bus modules
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/fr/products/specs/superior-multitransmitter-fibra/
  {
    id: 'ajax-superior-multitransmitter-fibra',
    manufacturer: 'Ajax',
    model: 'Superior MultiTransmitter Fibra',
    category: 'wireless-expander',
    tagline: 'Grade 3 wired module for 18 third-party NC/NO/EOL zones on a Fibra line, mains with 7 Ah backup',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-multitransmitter-fibra/',
    specs: {
      zonesPerModule: 18,
      busConnection: 'Fibra line, up to 2,000 m',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-multitransmitter-g3-jeweller/
  {
    id: 'ajax-superior-multitransmitter-g3-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior MultiTransmitter G3 Jeweller',
    category: 'wireless-expander',
    tagline: 'Grade 3 wireless module for 18 third-party wired zones, mains with 7 Ah backup, up to 3,500 m to the hub',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-multitransmitter-g3-jeweller/',
    specs: {
      frequency: SUPERIOR_JEWELLER_BANDS,
      biDirectional: true,
      zonesPerModule: 18,
      busConnection: 'Superior Jeweller radio to hub or range extender',
      rangeM: 3500,
    },
  },

  // https://ajax.systems/fr/products/specs/superior-multitransmitter-io-4x4-fibra/
  {
    id: 'ajax-superior-multitransmitter-io-4x4-fibra',
    manufacturer: 'Ajax',
    model: 'Superior MultiTransmitter IO (4X4) Fibra',
    category: 'wireless-expander',
    tagline: 'Grade 3 wired Fibra module, 4 inputs and 4 outputs (2 relay, 2 logic)',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-multitransmitter-io-4x4-fibra/',
    specs: {
      zonesPerModule: 4,
      busConnection: 'Fibra line, up to 2,000 m',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-transmitter-fibra/
  {
    id: 'ajax-superior-transmitter-fibra',
    manufacturer: 'Ajax',
    model: 'Superior Transmitter Fibra',
    category: 'wireless-expander',
    tagline: 'Grade 3 wired Fibra module bringing one third-party device onto the system, 2 inputs',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-transmitter-fibra/',
    specs: {
      zonesPerModule: 2,
      busConnection: 'Fibra line, up to 2,000 m',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-lineprotect-fibra/
  {
    id: 'ajax-superior-lineprotect-fibra',
    manufacturer: 'Ajax',
    model: 'Superior LineProtect Fibra',
    category: 'wireless-expander',
    tagline: 'Grade 3 Fibra line protection against short circuits, sabotage and surges, powered by the hub',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-lineprotect-fibra/',
    specs: {
      busConnection: 'Fibra line, up to 2,000 m',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-linesplit-fibra/
  {
    id: 'ajax-superior-linesplit-fibra',
    manufacturer: 'Ajax',
    model: 'Superior LineSplit Fibra',
    category: 'wireless-expander',
    tagline: 'Grade 3 module splitting one Fibra line into four, powered by the hub',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-linesplit-fibra/',
    specs: {
      busConnection: 'Fibra line, up to 2,000 m',
    },
  },

  // https://ajax.systems/products/specs/superior-linesupply-75w-fibra/ (both versions)
  {
    id: 'ajax-superior-linesupply-45w-fibra',
    manufacturer: 'Ajax',
    model: 'Superior LineSupply (45 W) Fibra',
    category: 'wireless-expander',
    tagline: 'Additional Fibra power supply, 1 output line, 30 W for devices, 12 V 7–17 Ah backup battery, fits Case C or D',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/superior-linesupply-fibra/',
    specs: {
      busConnection: 'Fibra line',
    },
  },

  // https://ajax.systems/products/specs/superior-linesupply-75w-fibra/
  {
    id: 'ajax-superior-linesupply-75w-fibra',
    manufacturer: 'Ajax',
    model: 'Superior LineSupply (75 W) Fibra',
    category: 'wireless-expander',
    tagline: 'Additional Fibra power supply, 2 output lines, 30 W each, 12 V 7–17 Ah backup battery, fits Case C or D',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-linesupply-75w-fibra/',
    specs: {
      busConnection: 'Fibra line',
    },
  },

  // https://ajax.systems/fr/products/specs/superior-multirelay-fibra/
  {
    id: 'ajax-superior-multirelay-fibra',
    manufacturer: 'Ajax',
    model: 'Superior MultiRelay Fibra',
    category: 'wireless-expander',
    tagline: 'Grade 3 wired Fibra module with 4 dry-contact relays, 10 A at 230 V~ / 5 A at 30 V⎓',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-multirelay-fibra/',
    specs: {
      busConnection: 'Fibra line, up to 2,000 m',
    },
  },
];
